import { trpc } from "../trpc";
import {
  findFolderInTree,
  FolderTreeItem,
  insertFolderIntoTree,
  removeFolderFromTree,
} from "../folderTree";
import type { FolderTreeOutput } from "@zapier/bff";

interface DeleteFolderAncestorsOptions {
  optimisticUpdateEnabled?: boolean;
}

export function useDeleteFolderAncestors({
  optimisticUpdateEnabled = true,
}: DeleteFolderAncestorsOptions) {
  const utils = trpc.useUtils();

  const mutation = trpc.folder.deleteFolderAncestors.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ folderId }) => {
      // Update Folder under folderTree key
      utils.folder.folderTree.cancel();
      utils.folder.folderAncestors.cancel();
      utils.folder.folderShares.cancel();

      // Don't optimistic update on folders listing page due to row being deleted, notification will not be shown
      if (optimisticUpdateEnabled) {
        utils.folder.folderTree.setData(
          {},
          (old: FolderTreeOutput | undefined) => {
            if (old?.hash) {
              // Grab the folder to update
              const folder = findFolderInTree(old.tree, folderId);

              // Update the folder with the new parentId and rootId -> move to top level
              const updatedFolder = {
                ...folder,
                rootId: null,
                parentId: null,
              } as FolderTreeItem;

              // Remove the folder from the tree
              const folderTreeWithFolderRemoved = removeFolderFromTree(
                old.tree,
                folderId,
              );

              // Insert updated folder into tree
              const updatedFolderTree = insertFolderIntoTree(
                folderTreeWithFolderRemoved,
                updatedFolder,
              );

              // this needs to return a new object ref for RQ to detect the change
              return {
                ...old,
                // update hash
                hash: {
                  ...old.hash,
                  [folderId]: updatedFolder,
                },
                // update tree
                tree: updatedFolderTree as FolderTreeItem[],
              };
            }

            return old;
          },
        );
      }
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate folderTree key to refetch data
      utils.folder.folderTree.invalidate();
      utils.folder.folderAncestors.invalidate();
      utils.folder.folderShares.invalidate();
      // Keep copy-preview subtree counts fresh after folder unnest operations.
      utils.folder.listAssetsInFolderSubtree.invalidate();
      utils.folder.listFolderSubtreeMaxDepth.invalidate();
    },
  });

  return {
    deleteFolderAncestors: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
